/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import {
	ChangeDetectionStrategy,
	Component,
	type OnDestroy,
	computed,
	effect,
	inject,
	input,
	output,
	signal,
} from "@angular/core";
import { IconComponent } from "@app/widgets/base/icon/icon.component";
import { TranslateModule } from "@ngx-translate/core";
import { type Observable, Subject, finalize, takeUntil } from "rxjs";
import type { Confirmation } from "../confirmation";

import { CommonModule } from "@angular/common";
import { ActionLoggerService } from "@app/@services/generic/action-logger.service";

@Component({
	selector: "cb-confirmation-widget",

	imports: [TranslateModule, IconComponent, CommonModule],
	templateUrl: "./confirmation-widget.component.html",
	styleUrls: ["./confirmation-widget.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationWidgetComponent implements OnDestroy {
	private readonly actionLoggerService: ActionLoggerService =
		inject(ActionLoggerService);

	public readonly resolve: Subject<boolean> = new Subject<boolean>();
	public readonly destroy$: Subject<void> = new Subject<void>();

	// Progress bar properties
	public readonly progressWidth = signal<number>(0);
	private timeoutId: number | null = null;
	private readonly startTime = Date.now();

	public showCancelButton = computed(
		() => this.confirmation()?.cancelButton ?? true,
	);

	public color = computed(() => this.confirmation()?.color ?? "brand");

	public readonly confirmation = input<Confirmation>();

	public readonly destroy = output<void>();

	constructor() {
		// Start timeout when confirmation changes
		effect(() => {
			const conf = this.confirmation();
			if (conf?.duration) {
				this.startTimeout(conf.duration);
			}
		});
	}

	public observe(): Observable<boolean> {
		return this.resolve.asObservable().pipe(
			takeUntil(this.destroy$),
			finalize(() => this.destroy.emit()),
		);
	}

	public selectOption(value: boolean): void {
		this.clearTimeout();
		this.resolve.next(value);
		this.destroy.emit();

		this.actionLoggerService.registerAction(
			`Confirmation dialog option selected: "${value}"`,
		);
	}

	private startTimeout(duration: number): void {
		this.clearTimeout();
		this.progressWidth.set(0);

		const durationMs = duration * 1000;
		const startTime = Date.now();

		const updateProgress = () => {
			const elapsed = Date.now() - startTime;
			const progress = Math.min((elapsed / durationMs) * 100, 100);
			this.progressWidth.set(progress);

			if (progress >= 100) {
				this.selectOption(true); // Auto-confirm
			} else {
				this.timeoutId = requestAnimationFrame(updateProgress);
			}
		};

		this.timeoutId = requestAnimationFrame(updateProgress);
	}

	private clearTimeout(): void {
		if (this.timeoutId !== null) {
			cancelAnimationFrame(this.timeoutId);
			this.timeoutId = null;
		}
	}

	ngOnDestroy(): void {
		this.clearTimeout();
		this.destroy$.next();
		this.destroy$.complete();
	}
}
